import { DateTime } from 'luxon'
import { type Descendant, Text } from 'slate'

import logger from '@/utils/logger'

import deserializeRichText from './deserializeRichText'
import type { SerializedRichText } from './types'

function getPlainTextInner(nodes: Descendant[]): string {
  return nodes
    .map(node => {
      if (Text.isText(node)) {
        return node.text
      }

      switch (node.type) {
        case 'bulleted-list': {
          return `${getPlainTextInner(node.children)}`
        }
        case 'numbered-list': {
          return `${getPlainTextInner(node.children)}`
        }
        case 'link': {
          return `[${getPlainTextInner(node.children)}](${node.url})`
        }
        case 'math': {
          return `${getPlainTextInner(node.children)}\n`
        }
        case 'image': {
          return `[](${node.url})\n`
        }
        case 'heading': {
          return `${getPlainTextInner(node.children)}\n\n`
        }
        case 'heading-two': {
          return `${getPlainTextInner(node.children)}\n\n`
        }
        case 'list-item': {
          return `  - ${getPlainTextInner(node.children)}\n`
        }
        case 'mention': {
          return `@${node.displayName}`
        }
        case 'date-time': {
          return DateTime.fromISO(node.dateTimeISO).toLocaleString(DateTime.DATETIME_FULL)
        }
        case 'paragraph': {
          return `${getPlainTextInner(node.children)}\n\n`
        }
        case 'template': {
          return node.readOnlyText
        }
        default: {
          return `${getPlainTextInner(node.children)}`
        }
      }
    })
    .join('')
}

export default function getPlainText(value: SerializedRichText | null): string {
  if (value == null || value.serialization_method === 'raw_html') {
    if (value?.serialization_method === 'raw_html') {
      if (value.source != null) {
        return value.source
      }

      logger.warning('getPlainText called for raw html without a source.')
    }

    return '[THIS CONTENT CANNOT BE DISPLAYED]'
  }

  if (value.serialization_method === 'plain_text') {
    return value.content
  }

  // Typescript safety net for the above check
  value.serialization_method satisfies 'slate_json_v1'

  return getPlainTextInner(deserializeRichText(value))
}
